import React, { useEffect, useState } from 'react';
import { Modal, Notification, STATUS_TYPES, generateResolver, VALIDATORS, yup, SearchableOptions, Person, USER_PERMISSIONS, PRODUCT_PERMISSIONS } from 'dyl-components';
import { Button, Form, Grid, Header, Icon, Segment } from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';

import { CustomGroupUtils, PhoneUtil, ValidationUtils } from 'utils';

import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

import './AddContactModal.scss';
import contactsActions from 'actions/contacts';
import customFieldsGroupActions from "actions/custom_fields_group";
import contactActions from 'actions/contact';
import AccountOptions from 'shared/forms/AccountOptions';
import ContactFields from 'shared/forms/ContactFields';
import CustomData from 'shared/CustomData';

import { commsSchema } from 'shared/schemas/contact/commsSchema';
import PHONE_TYPE_OPTIONS from 'shared/constants/PHONE_TYPE_OPTIONS';
import EMAIL_TYPE_OPTIONS from 'shared/constants/EMAIL_TYPE_OPTIONS';
import accountActions from 'actions/account';
import applyCurrencySchema from 'shared/schemas/products/applyCurrencySchema';
import RestrictedView from 'shared/RestrictedView';

const ModalContent = ({
    onClose,
    onDirtyChange,
    isOfficeView,
    unknownNumber,
    unknownCnam,
    isNewContactMode,
    setNewContactMode,
    existingContactMode, 
    setExistingContactMode,
    onSuccessCallback,
    isRecent,
    onRecentCallReload
}) => {
    const LIMIT = 15;
    const [contactSearch, setContactSearch] = useState("");
    const [contactOptions, setContactOptions] = useState([]);
    const [contactCount, setContactCount] = useState(0);
    const [isContactSearchOpen, setIsContactSearchOpen] = useState(false);
    const [contactPage, setContactPage] = useState(1);

    const { group, isCreating, isCheckingDuplicates, isReadingModalContact, modal_contact, currentCallPhoneNumber } = useSelector(state => {
        const { isCreatingLocation } = state.account;
        const { isCreating: isCreatingContacts } = state.contacts;
        const custom_groups = (state.custom_fields_group.contact_standard_group.children || []).filter(group => !group.standard);
        return {
            isCreating: isCreatingLocation || isCreatingContacts,
            group: custom_groups,
            isCheckingDuplicates: state.contact_duplicates.isCheckingDuplicates,
            isReadingModalContact:  state.contact.isReadingModalContact,
            modal_contact: state.contact.modal_contact,
            currentCallPhoneNumber: state.officeView.currentCallPhoneNumber,
        };
    });

    const custom_groups = CustomGroupUtils.formatFields(group);

    const getLocation = (contact) => {
        if (contact.locations && contact.locations.length > 0) {
            const mainLocation = contact.locations?.find(({main}) => main);
            return mainLocation ? mainLocation : contact.locations[0];
        }
        return null;
    }

    const getDetails = (details, fieldName) => {
        return details?.fields?.find(({field_name}) => field_name === fieldName)?.value || "";
    }

    const getDefaultValues = (contact, account) => {
        let phones = [{ value: '', type: PHONE_TYPE_OPTIONS.keys.CELL, main: true }];
        if (contact?.phone) {
            const { data: phoneData } = contact.phone;
            const phonesAux = phoneData?.map((phone) => ({
                value: phone.phone,
                type: phone.phone_type,
                main: phone.main
            }))
            phones = [{ value: unknownNumber || '', type: PHONE_TYPE_OPTIONS.keys.CELL, main: false, new: true }, ...phonesAux];
        }

        let emails = [{ value: '', type: EMAIL_TYPE_OPTIONS.keys.WORK, main: true }];
        if (contact?.email) {
            const { data: emailData } = contact.email;
            const emailsAux = emailData?.map((email) => ({
                value: email.email,
                type: EMAIL_TYPE_OPTIONS.keys.WORK,
                main: email.main
            }))
            emails = emailsAux;
        }

        const business_details = contact.custom_data?.children?.find(({group_name}) => (group_name === "business_details"));
        const household_details = contact.custom_data?.children?.find(({group_name}) => (group_name === "household_details"));
        const customData = contact?.custom_data?.children?.filter(({ group_name }) => (
            group_name !== "business_details" && group_name !== "household_details"
        )).map((group) => {
            const completeGroup = custom_groups.find(({group_name}) => group.group_name === group_name);
            const result = {
                ...completeGroup,
                ...group,
            }
            return result;
        }) || [];
        const location = getLocation(contact);
        
        const industryField = getDetails(business_details, "industry_sector")
        let industrySector = null;
        let subIndustry = null;
        if (!!industryField) {
            const industryArray = industryField.split(",");
            if (industryArray.length === 2) {
                industrySector = Number(industryArray[0])
                subIndustry = Number(industryArray[1])
            } else {
                industrySector = Number(industryArray[0])
                subIndustry = null;
            }
        }

        const { id } = account || {};
        const name = account?.custom_data?.fields?._business_name || null;
        const accountObject = { id, name, isAlreadyLinked: true };

        return {
            first_name: contact ? (contact.first_name || '') : '',
            last_name: contact ? (contact.last_name || '') : '',
            suffix: contact ? (contact.suffix || '') : '',
            birthday: contact && contact.date_of_birth && contact.date_of_birth !== '01-01-0001' ? contact.date_of_birth : null,
            phones,
            emails,
            website: contact ? (contact.website || '') : '',
            interests: [],
            department: contact ? getDetails(business_details, "department") : '',
            job_title: contact ? (contact.job_title || '') : '',

            address_label: location ? (location.label || '') : '',
            street: location ? (location.street || '') : '',
            apartmentUnitOrFloor: location ? (location.additional_street || '') : '',
            city: location ? (location.city || '') : '',
            state: location ? (location.state || '') : null,
            zip: location ? (location.zip || '') : '',

            account: id ? accountObject : null,

            business_name: contact ? getDetails(business_details, "company_name") : '',
            industry_sector: industrySector,
            sub_industry: subIndustry,
            employee_size: contact ? getDetails(business_details, "employee_count") : '',
            annual_revenue: contact ? getDetails(business_details, "annual_revenue") : '',
            product_services_sold: contact ? getDetails(business_details, "product_services_sold") : '',

            household_name: contact ? getDetails(household_details, "household_name") || '' : '',
            household_type: contact ? getDetails(household_details, "household_type") || null : null,
            household_members: contact ? getDetails(household_details, "household_members_count") || '' : '',
            number_of_children: contact ? getDetails(household_details, "number_of_children") || 0 : 0,
            annual_household_income: contact ? getDetails(household_details, "annual_income") || '' : '',

            children: contact ? customData : custom_groups
        }
    }

    const EMPTY_DEFAULT_VALUES = {
        id: null,
        first_name: '',
        last_name: '',
        suffix: '',
        birthday: '',
        phones: [{ value: '', type: PHONE_TYPE_OPTIONS.keys.CELL, main: true }],
        emails: [{ value: '', type: EMAIL_TYPE_OPTIONS.keys.WORK, main: true }],
        website: '',
        interests: [],
        department: '',
        job_title: '',

        address_label: 'Business',
        street: '',
        apartmentUnitOrFloor: '',
        city: '',
        state: null,
        zip: '',

        account: null,

        business_name: '',
        industry_sector: null,
        sub_industry: null,
        employee_size: 0,
        annual_revenue: 0,
        product_services_sold: '',

        household_name: '',
        household_type: null,
        household_members: 0,
        number_of_children: 0,
        annual_household_income: 0,

        children: custom_groups
    }

    const { formState: { isDirty, isValid, errors }, trigger, control, handleSubmit, watch, setError, setValue, reset } = useForm({
        mode: 'onChange',
        defaultValues: EMPTY_DEFAULT_VALUES,
        resolver: generateResolver({
            first_name: VALIDATORS.FIRST_NAME().no_whitespace_only().required('This field is required'),
            last_name: VALIDATORS.LAST_NAME().no_whitespace_only().required('This field is required'),
            ...commsSchema,
            suffix: VALIDATORS.SUFFIX().noemoji(),
            state: yup.string().required('This field is required'),
            street: yup.string().maxlength(100).minlength(2).noemoji(),
            apartmentUnitOrFloor: yup.string().maxlength(12).minlength(2).noemoji(),
            city: yup.string().maxlength(60).minlength(2).noemoji(),
            department: yup.string().maxlength(64).noemoji(),
            job_title: yup.string().noemoji().maxlength(60),
            zip: VALIDATORS.US_POSTAL_CODE(),
            website: VALIDATORS.WEBSITE().maxlength(256),
            children: CustomGroupUtils.generateValidationSchema(custom_groups),
            birthday: ValidationUtils.NEW_CONTACT_VALIDATIONS.birthday,
            business_name: VALIDATORS.NO_EMOJI().nullable(),
            employee_size: yup.number("Please enter a valid number").transform((value, originalValue) => originalValue === "" ? null : value).min(0, "Must be 0 or greater").typeError("Please enter a valid number").nullable(),
            product_services_sold: VALIDATORS.NO_EMOJI(),
            annual_revenue: applyCurrencySchema(yup.number().transform((value, originalValue) => originalValue === "" ? null : value), undefined, 0, true).typeError("Please enter a valid number").nullable(),
            household_name: VALIDATORS.NO_EMOJI(),
            household_members: yup.number().transform((value, originalValue) => originalValue === "" ? null : value).min(0, "Must be 0 or greater").typeError("Please enter a valid number").nullable(),
            number_of_children: yup.number().transform((value, originalValue) => originalValue === "" ? null : value).min(0, "Must be 0 or greater").typeError("Please enter a valid number").nullable(),
            annual_household_income: applyCurrencySchema(yup.number().transform((value, originalValue) => originalValue === "" ? null : value), undefined, 0, true).typeError("Please enter a valid number").nullable(),
        }, ['emails', 'phones'])
    });

    const [watchedPhones, watchedEmails] = watch(['phones', 'emails']);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const onCreateContacts = (contacts, account_id) => {
        return dispatch(contactsActions.createContact(contacts, { account_id }));
    }

    const onCreate = async (data) => {
        const custom_data = {
            fields: CustomGroupUtils.extractPersonFields(data),
            children: {
                ...CustomGroupUtils.extractBusinessAndHouseholdDetails(data),
                ...CustomGroupUtils.groupAndFlatten({ children: data.children }).children
            }
        }

        const { address_label, street, apartmentUnitOrFloor, city, state, zip } = data;

        const payload = [
            {
                custom_data,
                date_of_birth: data.birthday,
                email: watchedEmails.filter(emailItem => emailItem.value).map(({ value: email, main, type }) => ({
                    main,
                    email,
                    email_type: type
                })),
                first_name: data.first_name,
                last_name: data.last_name,
                suffix: data.suffix,
                influence: "influencer",
                location: [
                    {
                        street,
                        additional_street: apartmentUnitOrFloor,
                        city,
                        state,
                        zip,
                        label: address_label,
                        main: true,
                        shipping: true,
                        billing: true
                    }
                ],
                phone: watchedPhones.filter((phone) => phone.value).map(({ value, main, type }) => ({
                    main,
                    phone: PhoneUtil.getUnformatted(value),
                    phone_type: type
                })),
                job_title: data.job_title,
                website: data.website
            }
        ];

        try {
            const phones = payload?.[0]?.phone?.map(({phone}) => phone) || [];
            const currentPhone = currentCallPhoneNumber ? PhoneUtil.sanitizePhoneNumber(currentCallPhoneNumber) : null;
            const [contact_id] = await onCreateContacts(payload, data.account?.id);
            await dispatch(contactActions.getRecordsLimits());
            if (isOfficeView && !!currentPhone && phones.includes(currentPhone)) {
                await dispatch(contactsActions.viewLookup(contact_id));
            }
            if(isRecent) {
                onRecentCallReload()
            }
            Notification.alert('Successfully created contact!', STATUS_TYPES.SUCCESS);
            if (onSuccessCallback && typeof onSuccessCallback == "function") {
                onSuccessCallback();
            }
            onClose();
            if (!isOfficeView) {
                if (typeof (contact_id) == 'number') { navigate(`/contact/${contact_id}`); }
            }
        } catch (e) {
            console.log(e);
            Notification.alert('Failed to create contact', STATUS_TYPES.ERROR);
        }
    }

    const onUpdate = async (data) => {
        const custom_data = {
            fields: CustomGroupUtils.extractPersonFields(data),
            children: {
                ...CustomGroupUtils.extractBusinessAndHouseholdDetails(data),
                ...CustomGroupUtils.groupAndFlatten({ children: data.children }).children
            }
        }

        const { address_label, street, apartmentUnitOrFloor, city, state, zip } = data;

        const payload = {
            custom_data,
            date_of_birth: data.birthday,
            email: watchedEmails.filter(emailItem => emailItem.value).map(({ value: email, main, type }) => ({
                main,
                email,
                email_type: type
            })),
            first_name: data.first_name,
            last_name: data.last_name,
            suffix: data.suffix,
            influence: "influencer",
            location: [
                {
                    street,
                    additional_street: apartmentUnitOrFloor,
                    city,
                    state,
                    zip,
                    label: address_label,
                    main: true,
                    shipping: true,
                    billing: true
                }
            ],
            phone: watchedPhones.filter((phone) => phone.value).map(({ value, main, type }) => ({
                main,
                phone: PhoneUtil.getUnformatted(value),
                phone_type: type
            })),
            job_title: data.job_title,
            website: data.website,
            person_id: modal_contact.contact.id
        };

        try {
            const phones = payload?.phone?.map(({phone}) => phone) || [];
            const currentPhone = currentCallPhoneNumber ? PhoneUtil.sanitizePhoneNumber(currentCallPhoneNumber) : null;
            await dispatch(accountActions.updateFullPerson(payload));
            if (!!currentPhone && phones.includes(currentPhone)) {
                await dispatch(contactsActions.viewLookup(modal_contact.contact.id));
            }
            if (isRecent) {
                onRecentCallReload()
            }
            Notification.alert('Successfully updated contact!', STATUS_TYPES.SUCCESS);
            if (onSuccessCallback && typeof onSuccessCallback == "function") {
                onSuccessCallback();
            }
            onClose();
        } catch (e) {
            console.log(e);
            Notification.alert('Failed to update contact', STATUS_TYPES.ERROR);
        }
    }

    const onReadContacts = (queryParameters) => {
        return dispatch(contactActions.getContactOptions(queryParameters));
    }
    
    const searchFunction = async () => {
        try {
            const { data, count } = await onReadContacts({ page: 1, search: contactSearch, limit: LIMIT });
            setContactOptions(data);
            setContactCount(count);
            setIsContactSearchOpen(true);
            setContactPage(1);
        } catch (e) {
            console.log(e);
        }
    }

    const cancelFunction = () => {
        setContactOptions([]);
        setContactCount(0);
        setIsContactSearchOpen(false);
        setContactPage(1);
        setContactSearch("");
        setNewContactMode(false);
        setExistingContactMode(false);

        reset({
            ...EMPTY_DEFAULT_VALUES,
            first_name: "",
            last_name: "",
            phones: [{ value: unknownNumber || '', type: PHONE_TYPE_OPTIONS.keys.CELL, main: true }],
        });
    }

    const onSelectNew = () => {
        const [newName, ...rest] = contactSearch.trim().split(" ");
        const newLastName = rest.join(" ");

        setContactOptions([]);
        setContactCount(0);
        setIsContactSearchOpen(false);
        setContactPage(1);
        setContactSearch("New Contact");
        setNewContactMode(true);
        setExistingContactMode(false);

        reset({
            ...EMPTY_DEFAULT_VALUES,
            first_name: newName || "",
            last_name: newLastName || "",
            phones: [{ value: unknownNumber || '', type: PHONE_TYPE_OPTIONS.keys.CELL, main: true }],
        });
    }

    const onSelectContact = async (id) => {
        const { contact, account } = await dispatch(contactActions.readModalContact(id));
        const newValues = getDefaultValues(contact, account);
        reset(newValues);
        trigger();
    }

    const onChangeSearch = (_, { value }) => {
        setContactSearch(value);
        const isOpen = (value?.trim().length >= 2 && contactCount > 0)
        setIsContactSearchOpen(isOpen);
    }

    const hasErrors = Object.keys(errors).length > 0;
    useEffect(() => {
        if (onDirtyChange) {
            onDirtyChange(isDirty);
        }
        // eslint-disable-next-line
    }, [isDirty])

    useEffect(() => {
        if (unknownCnam) {
            setContactSearch(unknownCnam);
        } else {
            setContactSearch("");
        }
    }, [unknownCnam])
    
    const displayForm = !isOfficeView || (isOfficeView && isNewContactMode) || (isOfficeView && existingContactMode);

    return (
        [
            <Modal.Content scrolling>
                <Form noValidate loading={isReadingModalContact}>
                    {isOfficeView && (
                        <Controller
                            name='contact'
                            control={control}
                            render={({ field: { onChange } }) => (
                                <Form.Field
                                    required
                                    control={SearchableOptions}
                                    disabled={false}
                                    label="Contact Name"
                                    loading={false}
                                    search={contactSearch}
                                    searchFunction={searchFunction}
                                    cancelSearchFunction={cancelFunction}
                                    searchPlaceholder={'Search contact'}
                                    onChangeSearch={onChangeSearch}
                                    isSearchResultsVisible={isContactSearchOpen}
                                    renderSearchResults={
                                        <React.Fragment>
                                            {contactSearch.trim().length > 1 && (
                                                <RestrictedView type="link" userPermission={USER_PERMISSIONS.CONTACTS_ADD} productPermission={PRODUCT_PERMISSIONS.RECORDS}>
                                                    <Segment style={{ cursor: 'pointer' }} onClick={onSelectNew}>
                                                        + Add<span className='AddContactModal__ContactOptionAsNew'>{contactSearch}</span>' as New Contact
                                                    </Segment>
                                                </RestrictedView>
                                            )}
                                            {contactSearch.trim().length > 1 && contactCount > 0 && (
                                                <Segment loading={false}>
                                                    <Grid>
                                                        {contactOptions.map(contactOption => {
                                                            const { first_name, last_name, id, account, email, phone } = contactOption;
                                                            const name = `${first_name} ${last_name}`;
                                                            return (
                                                                <Grid.Row verticalAlign='middle' key={id} columns='equal' stretched>
                                                                    <Grid.Column width={4}>
                                                                        <Person
                                                                            username={name}
                                                                            email={email.email}
                                                                        />
                                                                    </Grid.Column>
                                                                    <Grid.Column width={2}>
                                                                        ID: {id}
                                                                    </Grid.Column>
                                                                    <Grid.Column>
                                                                        {PhoneUtil.formatPhoneNumber(phone.phone)}
                                                                    </Grid.Column>
                                                                    <Grid.Column>
                                                                        {account && account.id && (
                                                                            <span><Icon className={`fas fa-${account.account_type === 'business' ? 'building' : 'house'}`} />{account.name}</span>
                                                                        )}
                                                                    </Grid.Column>
                                                                    <Grid.Column width={1}>
                                                                        <Icon target="_blank" as={Link} to={`/contact/${id}`} className='fas fa-external-link' />
                                                                    </Grid.Column>
                                                                    <Grid.Column width={3}>
                                                                        <Button color='primary' onClick={() => { 
                                                                            onChange({ target: { name:'contact', value: id } });
                                                                            setNewContactMode(false);
                                                                            setExistingContactMode(true);
                                                                            setContactOptions([]);
                                                                            setContactCount(0);
                                                                            setIsContactSearchOpen(false);
                                                                            setContactPage(1);
                                                                            setContactSearch(name)
                                                                            onSelectContact(id);
                                                                        }}>
                                                                            Add
                                                                        </Button>
                                                                    </Grid.Column>
                                                                </Grid.Row>
                                                            );
                                                        })}
                                                    </Grid>
                                                </Segment>  
                                            )}                                                              
                                        </React.Fragment>
                                    }
                                    isPaginationVisible={contactOptions.length > 0}
                                    paginationProps={{
                                        boundaryRange: 0,
                                        activePage: contactPage,
                                        ellipsisItem: null,
                                        siblingRange: 2,
                                        totalPages: Math.ceil(contactCount / LIMIT),
                                        onPageChange: async (_, { activePage }) => {
                                            setIsContactSearchOpen(true);
                                            setContactPage(activePage);
                                            const { data, count } = await onReadContacts({ page: activePage, search: contactSearch, limit: LIMIT });
                                            setContactPage(activePage);
                                            setContactCount(count);
                                            setIsContactSearchOpen(true)
                                            setContactOptions(data);
                                        }
                                    }}
                                />
                            )}
                        />
                    )} 
                    {displayForm && (
                        <>
                            <ContactFields
                                control={control}
                                setError={setError}
                                setValue={setValue}
                                isOfficeView={isOfficeView}
                                unknownNumber={unknownNumber}
                                existingContactMode={existingContactMode}
                            />
                            <Header as='h3' color='primary'>Account</Header>
                            <Controller
                                name='account'
                                control={control}
                                render={({ field: { name, value, onChange } }) => (
                                    <Form.Field
                                        name={name}
                                        value={value}
                                        control={AccountOptions}
                                        label='Link to Existing Account'
                                        onChange={(_, { value }) => {
                                            onChange({ target: { name, value } });
                                        }}
                                    />
                                )}
                            />
                            <Header as='h3' color='primary'>Custom Data</Header>
                            <CustomData
                                control={control}
                                isEditing
                                padded
                            />
                        </>
                    )}
                </Form>
            </Modal.Content>,
            <Modal.Actions>
                <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.CONTACTS_ADD}>
                    <Button 
                        disabled={(!isOfficeView && !isDirty) || !isValid || isCheckingDuplicates || hasErrors || isCreating} 
                        primary 
                        onClick={handleSubmit(existingContactMode ? onUpdate : onCreate )} 
                        loading={isCreating}
                    >
                        {existingContactMode ? "Save" : "Create"}
                    </Button>
                </RestrictedView>
            </Modal.Actions>
        ]
    )
}

const AddContactModal = ({
    onClose,
    isOfficeView,
    unknownNumber,
    unknownCnam,
    onSuccessCallback,
    isRecent,
    onRecentCallReload
}) => {
    const [isNewContactMode, setNewContactMode] = useState(false);
    const [existingContactMode, setExistingContactMode] = useState(false);

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(customFieldsGroupActions.getContactStandardGroup({ group_name: 'person' }));
    }, [dispatch]);

    const isReadingCustomFields = useSelector(state => state.custom_fields_group.isReadingContactStandardFieldGroup);

    return (
        <React.Fragment>
            <Modal.Header>
                {existingContactMode ? "Update Contact" : "Add Contact"}
            </Modal.Header>
            {isReadingCustomFields ? (
                <Segment loading basic></Segment>
            ) : (
                <ModalContent 
                    onClose={onClose}
                    isOfficeView={isOfficeView}
                    unknownNumber={unknownNumber}
                    unknownCnam={unknownCnam}
                    isNewContactMode={isNewContactMode}
                    setNewContactMode={setNewContactMode}
                    existingContactMode={existingContactMode}
                    setExistingContactMode={setExistingContactMode}
                    onSuccessCallback={onSuccessCallback}
                    isRecent={isRecent}
                    onRecentCallReload={onRecentCallReload}
                />
            )}
        </React.Fragment>
    )
}

export default AddContactModal;
